"use client"

import { Suspense, useState, useRef, useCallback, useEffect, useMemo } from "react"
import { usePathname, useSearchParams } from "next/navigation"
import { useLocale, useTranslations } from "next-intl"
import { getIntlLocale } from "@/shared/lib/i18n/config"
import { motion } from "framer-motion"
import { Coins, Loader2 } from "lucide-react"
import { Icon, type IconName } from "@/shared/components/icons"
import { Button } from "@/shared/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/shared/components/ui/dialog"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shared/components/ui/accordion"
import { browserLocalStorage, noteCheckoutStarted } from "@/shared/lib/sales-closer/hook"
import { cn } from "@/shared/lib/utils"
import { ROUTES } from "@/shared/lib/constants"
import { pricingPlans } from "@/features/pricing/data"
import { useAuth } from "@/features/auth/hooks/use-auth"
import {
  createCheckoutSession,
  changeSubscriptionTier,
  cancelPendingDowngrade,
  createCustomerPortalSession,
  reactivateSubscription,
} from "@/features/credits/actions"
import {
  ANNUAL_DISCOUNT_PERCENT,
  TIER_CONFIG,
  getYearlyMonthlyPrice,
  getTierDirection,
  isPaidTier,
} from "@/features/credits/constants"
import {
  monthlyShownAmount,
  parsePaywallAmountsResponse,
  yearlySavingsShown,
} from "@/features/credits/lib/stripe-paywall-amounts"
import type { PaywallAmounts } from "@/features/credits/lib/stripe-paywall-amounts"
import { useSubscription } from "@/features/credits/hooks/use-subscription"
import { useBillingPeriodExperiment } from "@/features/credits/hooks/use-billing-period-experiment"
import { usePaywallCtaLoadingState } from "@/features/credits/hooks/use-paywall-cta-loading-state"
import { normalizePaywallExperiment } from "@/features/credits/lib/paywall-experiment"
import { loggedOutRegisterHref } from "@/features/credits/lib/logged-out-plan-redirect"
import {
  CHECKOUT_CONFIDENCE_STRIP_FLAG,
  type CheckoutConfidenceStripVariant,
  normalizeConfidenceStripVariant,
  getPostHogForConfidenceStrip,
} from "@/features/credits/lib/checkout-confidence-strip-experiment"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { queryKeys } from "@/shared/lib/query-keys"
import { deferAuthStoreMutation, useAuthStore } from "@/features/auth/store"
import { getProfileForCurrentUser } from "@/features/profile"
import { useUIStore } from "@/shared/stores/ui-store"
import { track } from "@/shared/lib/analytics"
import { parseOpaqueCharacterId, parsePaywallIntent } from "@/shared/lib/paywall"
import { useLocalizedRouter } from "@/shared/hooks/use-localized-router"
import type { SubscriptionTier } from "@/shared/lib/supabase/types"
import { useCheckoutReassuranceExperiment } from "@/features/credits/hooks/use-checkout-reassurance-experiment"
import {
  CRO_CHECKOUT_REASSURANCE_FLAG,
  normalizeCheckoutReassuranceVariant,
} from "@/features/credits/lib/checkout-reassurance-experiment"

// ── FAQs ──────────────────────────────────────────────────────────────────────

const faqKeys = ["try", "cancel", "coins", "privacy", "change", "refund"] as const

// ── Animation ─────────────────────────────────────────────────────────────────

// ── Card Action Types ─────────────────────────────────────────────────────────

type CardAction =
  | { type: "register"; label: string }
  | { type: "subscribe"; label: string }
  | { type: "current"; label: string }
  | { type: "upgrade"; label: string }
  | { type: "downgrade"; label: string }
  | { type: "disabled"; label: string }
  | { type: "pending_downgrade"; label: string; sublabel: string }

type PlanTier = Extract<SubscriptionTier, "starter" | "premium" | "ultimate">
type PricingPlan = (typeof pricingPlans)[number]
type BillingPeriod = "monthly" | "yearly"
type CheckoutSource = "subscription_page" | "pricing_page" | "paywall_modal"
type CheckoutSelectedFrom = "query" | "current_subscription" | "default" | "manual"
type CheckoutPresentation = "page" | "modal"

const planMessageKeys: Record<PlanTier, "starter" | "standard" | "ultimate"> = {
  starter: "starter",
  premium: "standard",
  ultimate: "ultimate",
}

const checkoutSources: CheckoutSource[] = ["subscription_page", "pricing_page", "paywall_modal"]

function toPlanTier(tier: SubscriptionTier): PlanTier | null {
  if (tier === "starter" || tier === "premium" || tier === "ultimate") return tier
  return null
}

function parsePlanTier(value: string | null): PlanTier | null {
  if (value === "starter" || value === "premium" || value === "ultimate") return value
  return null
}

function parseBillingPeriod(value: string | null): BillingPeriod | null {
  if (value === "monthly" || value === "yearly") return value
  return null
}

function parseCheckoutSource(value: string | null): CheckoutSource {
  return checkoutSources.includes(value as CheckoutSource)
    ? (value as CheckoutSource)
    : "subscription_page"
}

function parseCheckoutSelectedFrom(value: string | null): CheckoutSelectedFrom | null {
  if (
    value === "query" ||
    value === "current_subscription" ||
    value === "default" ||
    value === "manual"
  ) {
    return value
  }
  return null
}

type PricingTranslator = (key: string, values?: Record<string, string | number>) => string

function getPrimaryActionLabel(action: CardAction, t: PricingTranslator): string {
  if (action.type === "register") return t("checkout.createAccount")
  if (action.type === "subscribe") return t("checkout.getStarted")
  if (action.type === "upgrade") return t("checkout.upgradePay")
  if (action.type === "downgrade") return t("checkout.scheduleDowngrade")
  if (action.type === "pending_downgrade") return t("checkout.cancelPlanChange")
  return action.label
}

function CheckoutReassuranceModule({ className }: { className?: string }) {
  const t = useTranslations("pricing")
  const reassuranceItems: { icon: IconName; text: string }[] = [
    { icon: "lock", text: t("checkout.instantPrivateAccess") },
    { icon: "tick-circle", text: t("checkout.cancelAnytime") },
    { icon: "shield", text: t("checkout.satisfactionGuarantee") },
    { icon: "shield-done", text: t("checkout.secureStripe") },
  ]
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] px-3 py-2.5",
        className
      )}
      role="list"
      aria-label={t("checkout.guaranteesLabel")}
    >
      {reassuranceItems.map((item) => (
        <div
          key={item.text}
          role="listitem"
          className="flex items-center gap-2 text-[11px] font-medium leading-snug text-emerald-200/90 sm:text-xs"
        >
          <Icon
            name={item.icon}
            size={12}
            className="size-3 shrink-0 text-emerald-400"
            aria-hidden
          />
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  )
}

function CheckoutConfidenceStrip({ className }: { className?: string }) {
  const t = useTranslations("pricing")
  return (
    <p
      className={cn(
        "flex items-center justify-center gap-1 text-[11px] font-medium text-foreground/[0.52] sm:text-xs",
        className
      )}
      role="note"
      aria-label={t("checkout.securityLabel")}
    >
      <Icon name="shield" size={12} className="size-3 shrink-0 text-emerald-400" aria-hidden />
      <span>{t("checkout.secureStripe")}</span>
      <span className="text-foreground/[0.28]" aria-hidden>-</span>
      <span>{t("checkout.privateBilling")}</span>
      <span className="text-foreground/[0.28]" aria-hidden>-</span>
      <span>{t("checkout.cancelAnytime")}</span>
    </p>
  )
}

function UpgradeCheckoutPanel({
  action,
  annualTotal,
  confidenceStripVariant,
  ctaErrorTier,
  isPaid,
  isYearly,
  loadingTier,
  onAction,
  onSelectTier,
  paywallCtaLoadingStateEnabled,
  presentation,
  selectedPlan,
  selectedTier,
  setIsYearly,
  showReassurance,
  stripeAmounts = null,
}: {
  action: CardAction
  annualTotal: number
  confidenceStripVariant: CheckoutConfidenceStripVariant
  ctaErrorTier: string | null
  isPaid: boolean
  isYearly: boolean
  loadingTier: string | null
  onAction: () => void
  onSelectTier: (tier: PlanTier) => void
  paywallCtaLoadingStateEnabled: boolean
  presentation: CheckoutPresentation
  selectedPlan: PricingPlan
  selectedTier: PlanTier
  setIsYearly: (value: boolean) => void
  showReassurance: boolean
  stripeAmounts?: PaywallAmounts | null
}) {
  const t = useTranslations("pricing")
  const locale = useLocale()
  const intlLocale = getIntlLocale(locale)
  const selectedPlanName = t(`plans.${planMessageKeys[selectedTier]}.name`)
  const selectedStripeSlot = stripeAmounts?.[selectedTier]
  const selectedSavings = yearlySavingsShown(
    selectedPlan.price,
    getYearlyMonthlyPrice(selectedPlan.price),
    selectedStripeSlot,
  )
  const selectedCoins = selectedPlan.coinsPerMonth
  const selectedStripeYearly = isYearly ? stripeAmounts?.[selectedTier] : null
  const selectedDisplayPrice = selectedStripeYearly
    ? selectedStripeYearly.annualMonthlyEquivalent
    : isYearly
      ? getYearlyMonthlyPrice(selectedPlan.price)
      : monthlyShownAmount(selectedTier, selectedPlan.price, stripeAmounts)
  const selectedImages = Math.floor(selectedCoins / 10)
  const selectedCalls = TIER_CONFIG[selectedTier].hasPhoneCall
    ? Math.floor(selectedCoins / 50)
    : null
  const isCurrentPlan = action.type === "current"
  const isSubscribeAction =
    action.type === "subscribe" ||
    action.type === "register" ||
    action.type === "upgrade"
  const showConfidenceStrip =
    confidenceStripVariant === "confidence_strip" && isSubscribeAction
  const ctaLabel = isSubscribeAction
    ? t("checkout.subscribeTo", { plan: selectedPlanName })
    : getPrimaryActionLabel(action, t)

  const benefitGroups: Array<{
    icon: IconName | "coins"
    title: string
    details: string[]
  }> = [
    {
      icon: "coins",
      title: t("checkout.benefitCoins", { count: selectedCoins.toLocaleString(intlLocale) }),
      details: [
        t("checkout.benefitImages", { count: selectedImages.toLocaleString(intlLocale) }),
        selectedCalls
          ? t("checkout.benefitCallMinutes", { count: selectedCalls.toLocaleString(intlLocale) })
          : t("checkout.voiceNotes"),
      ],
    },
    {
      icon: "chat" as IconName,
      title: t("checkout.unlimitedMessages"),
      details: [t("checkout.noDailyCap"), t("checkout.premiumAccess")],
    },
    {
      icon: "voice" as IconName,
      title: t("checkout.unlimitedAudio"),
      details: [t("checkout.voiceReplies")],
    },
    {
      icon: "image" as IconName,
      title: t("checkout.imageGeneration"),
      details: [t("checkout.useCoins")],
    },
    {
      icon: "phone" as IconName,
      title: t("checkout.voiceCalls"),
      details: selectedCalls ? [t("checkout.callPrice")] : [t("checkout.upgradeCalls")],
    },
    {
      icon: "heart" as IconName,
      title: t("checkout.createCompanion"),
      details: [t("checkout.customizeCompanion")],
    },
  ]

  return (
    <section
      className={cn(
        "relative px-[max(1rem,env(safe-area-inset-left))] pb-28 pr-[max(1rem,env(safe-area-inset-right))] sm:pb-8",
        presentation === "modal"
          ? "pt-6 sm:pt-8"
          : "pt-[max(1rem,env(safe-area-inset-top))] lg:pb-12 lg:pt-16"
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(29,125,242,0.16),transparent_34%),radial-gradient(circle_at_50%_56%,rgba(255,255,255,0.045),transparent_28%)]"
      />
      <div className="relative z-10 mx-auto max-w-[34.5rem] rounded-[1.35rem] border border-white/[0.12] bg-[#0d0d10]/[0.96] p-4 shadow-[0_24px_90px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,255,255,0.06)] sm:rounded-[1.5rem] sm:p-8">
        <div className="text-center">
          <h2 className="text-[2.35rem] font-black leading-none tracking-normal text-white sm:text-5xl">
            <span className="bg-[linear-gradient(90deg,#ffffff,#1d7df2_58%,#8cc5ff)] bg-clip-text text-transparent">
              {isPaid ? t("checkout.managePremium") : t("checkout.goPremium")}
            </span>
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-[13px] font-medium leading-5 text-white/[0.54] sm:mt-3 sm:text-sm sm:leading-6">
            {isPaid
              ? t("checkout.manageSubtitle")
              : t("checkout.unlockSubtitle")}
          </p>
        </div>

        <div
          className="mx-auto mt-5 flex w-full max-w-[26rem] rounded-full border border-white/[0.08] bg-white/[0.08] p-1 sm:mt-7 sm:w-fit"
          role="group"
          aria-label={t("checkout.billingPeriod")}
        >
          <button
            type="button"
            onClick={() => setIsYearly(false)}
            className={cn(
              "min-h-10 flex-1 rounded-full px-4 text-sm font-black transition-colors sm:flex-none sm:px-5",
              !isYearly ? "bg-white/[0.18] text-white" : "text-white/[0.46] hover:text-white"
            )}
          >
            {t("monthly")}
          </button>
          <button
            type="button"
            onClick={() => setIsYearly(true)}
            className={cn(
              "flex min-h-10 flex-[1.35] items-center justify-center gap-1.5 rounded-full px-3 text-sm font-black transition-colors sm:flex-none sm:gap-2 sm:px-5",
              isYearly ? "bg-white/[0.18] text-white" : "text-white/[0.46] hover:text-white"
            )}
          >
            {t("yearly")}
            <span className="rounded-full bg-emerald-400/[0.18] px-2 py-0.5 text-[10px] font-black text-emerald-200">
              {t("checkout.saveBonus", { percent: ANNUAL_DISCOUNT_PERCENT })}
            </span>
          </button>
        </div>

        <div className="mt-4 grid gap-2.5 sm:mt-5 sm:grid-cols-3 sm:gap-3">
          {pricingPlans.map((plan) => {
            const planTier = plan.tier as PlanTier
            const isSelected = selectedTier === planTier
            const stripeYearly = isYearly ? stripeAmounts?.[planTier] : null
            const displayPrice = stripeYearly
              ? stripeYearly.annualMonthlyEquivalent
              : isYearly
                ? getYearlyMonthlyPrice(plan.price)
                : monthlyShownAmount(planTier, plan.price, stripeAmounts)
            const yearlySavings = yearlySavingsShown(
              plan.price,
              getYearlyMonthlyPrice(plan.price),
              stripeAmounts?.[planTier],
            )

            return (
              <button
                key={plan.tier}
                type="button"
                onClick={() => onSelectTier(planTier)}
                aria-pressed={isSelected}
                className={cn(
                  "relative min-h-[5.85rem] rounded-[1.15rem] border bg-white/[0.035] p-3 text-left transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring sm:min-h-[8.25rem] sm:rounded-2xl",
                  isSelected
                    ? "border-accent-blue bg-accent-blue/[0.11] shadow-[0_0_0_1px_rgba(29,125,242,0.34),0_14px_45px_rgba(29,125,242,0.16)]"
                    : "border-white/[0.10] hover:border-white/[0.22]"
                )}
              >
                {planTier === "premium" ? (
                  <span className="absolute -top-2 right-3 rounded-full bg-[#ffd230] px-2 py-0.5 text-[9px] font-black uppercase text-black">
                    {t("checkout.bestValue")}
                  </span>
                ) : null}
                <div className="flex items-start justify-between gap-3 sm:block">
                  <p
                    className={cn(
                      "pr-20 text-sm font-black uppercase leading-none sm:pr-0",
                      isSelected ? "text-accent-blue" : "text-white/[0.82]"
                    )}
                  >
                    {t(`plans.${planMessageKeys[planTier]}.name`)}
                  </p>
                  <div className="flex shrink-0 items-baseline gap-1 tabular-nums sm:mt-3">
                    <span className="text-2xl font-black tracking-normal text-white">
                      {formatUsd(displayPrice, intlLocale)}
                    </span>
                    <span className="text-[11px] font-bold text-white/[0.48]">{t("checkout.perMonth")}</span>
                  </div>
                </div>
                {stripeYearly ? (
                  <p className="mt-0 text-right text-[11px] font-bold text-white/[0.72] sm:mt-1 sm:text-left">
                    {t("checkout.billedYearly", { amount: formatUsd(stripeYearly.annual, intlLocale) })}
                  </p>
                ) : isYearly ? (
                  <p className="mt-0 text-right text-[11px] font-bold text-white/[0.34] line-through sm:mt-1 sm:text-left">
                    {formatUsd(plan.price, intlLocale)}{t("checkout.perMonth")}
                  </p>
                ) : null}
                <div className="mt-2 flex items-center justify-between gap-2 sm:block">
                  <p className="inline-flex items-center gap-1.5 text-xs font-black text-accent-blue">
                    <Coins className="size-3.5 shrink-0" aria-hidden />
                    +{t("checkout.coins", { count: plan.coinsPerMonth.toLocaleString(intlLocale) })}
                  </p>
                  {isYearly && yearlySavings > 0 ? (
                    <p className="text-[10px] font-bold text-white/[0.40] sm:mt-1">
                      {t("checkout.savePerYear", {
                        amount: formatNumber(yearlySavings, intlLocale),
                      })}
                    </p>
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>

        <div className="mt-4 text-center sm:mt-5">
          <p className="text-xs font-medium text-white/[0.44]">
            {isYearly
              ? t("checkout.billedYearly", { amount: formatUsd(annualTotal, intlLocale) })
              : t("checkout.billedMonthly")}
          </p>
          {isYearly && selectedSavings > 0 ? (
            <p className="mt-1 text-sm font-black text-emerald-300">
              {t("checkout.yearlyBonus", {
                amount: formatNumber(selectedSavings, intlLocale),
              })}
            </p>
          ) : null}
        </div>

        <CardActionButton
          action={action}
          className={cn(
            "group mt-6 hidden h-auto min-h-[3.25rem] overflow-hidden rounded-full border border-[#8cc5ff]/32 bg-[linear-gradient(180deg,#47b4ff_0%,#197cf0_44%,#084db8_100%)] px-5 py-3 text-base font-extrabold text-white shadow-[0_18px_54px_rgba(29,125,242,0.42),0_0_0_1px_rgba(140,197,255,0.14),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-16px_26px_rgba(0,24,84,0.34)] transition-[background,border-color,box-shadow,transform] duration-200 will-change-transform hover:-translate-y-1 hover:scale-[1.015] hover:border-[#b7dcff]/75 hover:bg-[linear-gradient(180deg,#73ccff_0%,#248fff_42%,#0a5bd2_100%)] hover:shadow-[0_0_0_1px_rgba(183,220,255,0.42),0_0_34px_rgba(82,171,255,0.44),0_28px_86px_rgba(29,125,242,0.62),inset_0_1px_0_rgba(255,255,255,0.42),inset_0_-18px_30px_rgba(0,22,78,0.42)] active:translate-y-0 active:scale-[0.985] sm:inline-flex",
            !isSubscribeAction &&
              "border-accent-blue/45 bg-[linear-gradient(180deg,rgba(52,151,255,0.24)_0%,rgba(10,30,52,0.98)_100%)] text-white shadow-[0_16px_42px_rgba(29,125,242,0.24),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-14px_24px_rgba(0,14,38,0.30)] hover:border-[#8cc5ff]/65 hover:bg-[linear-gradient(180deg,rgba(74,165,255,0.32)_0%,rgba(8,36,68,0.98)_100%)] hover:shadow-[0_0_0_1px_rgba(140,197,255,0.24),0_22px_64px_rgba(29,125,242,0.38),inset_0_1px_0_rgba(255,255,255,0.30),inset_0_-16px_28px_rgba(0,14,38,0.38)]"
          )}
          ctaErrorTier={ctaErrorTier}
          isCurrentPlan={isCurrentPlan}
          isHighlighted
          labelOverride={ctaLabel}
          loadingTier={loadingTier}
          onClick={onAction}
          paywallCtaLoadingStateEnabled={paywallCtaLoadingStateEnabled}
          planTier={selectedTier}
        />

        <div className="hidden sm:block">
          {showReassurance ? (
            <CheckoutReassuranceModule className="mt-3 border-white/[0.08] bg-white/[0.035]" />
          ) : showConfidenceStrip ? (
            <CheckoutConfidenceStrip className="mt-3" />
          ) : (
            <p className="mt-3 text-center text-xs font-medium text-white/[0.40]">
              {t("checkout.privateSecure")}
            </p>
          )}
        </div>

        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/[0.10] bg-[#08090b]/[0.92] px-[max(0.875rem,env(safe-area-inset-left))] pb-[max(0.875rem,env(safe-area-inset-bottom))] pr-[max(0.875rem,env(safe-area-inset-right))] pt-3 shadow-[0_-18px_60px_rgba(0,0,0,0.54)] backdrop-blur-xl sm:hidden">
          <div className="mx-auto max-w-[28rem]">
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-black text-white">
                  {selectedPlanName}
                </p>
                <p className="text-[11px] font-semibold text-white/[0.48]">
                  {isYearly
                    ? t("checkout.billedYearly", { amount: formatUsd(annualTotal, intlLocale) })
                    : t("checkout.billedMonthly")}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-lg font-black leading-none text-white tabular-nums">
                  {formatUsd(selectedDisplayPrice, intlLocale)}
                  <span className="ml-1 text-[11px] text-white/[0.48]">{t("checkout.perMonth")}</span>
                </p>
                <p className="mt-1 text-[11px] font-black text-emerald-300">
                  +{t("checkout.coins", { count: selectedCoins.toLocaleString(intlLocale) })}
                </p>
              </div>
            </div>
            <CardActionButton
              action={action}
              className={cn(
                "group h-auto min-h-[3.05rem] overflow-hidden rounded-full border border-[#8cc5ff]/32 bg-[linear-gradient(180deg,#55bdff_0%,#197cf0_48%,#0751bd_100%)] px-5 py-3 text-[15px] font-extrabold text-white shadow-[0_16px_46px_rgba(29,125,242,0.48),inset_0_1px_0_rgba(255,255,255,0.36),inset_0_-14px_24px_rgba(0,24,84,0.36)] active:scale-[0.985]",
                !isSubscribeAction &&
                  "border-accent-blue/45 bg-[linear-gradient(180deg,rgba(52,151,255,0.24)_0%,rgba(10,30,52,0.98)_100%)]"
              )}
              ctaErrorTier={ctaErrorTier}
              isCurrentPlan={isCurrentPlan}
              isHighlighted
              labelOverride={ctaLabel}
              loadingTier={loadingTier}
              onClick={onAction}
              paywallCtaLoadingStateEnabled={paywallCtaLoadingStateEnabled}
              planTier={selectedTier}
            />
            <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] font-medium text-white/[0.46]">
              <Icon name="shield" size={12} className="size-3 text-emerald-400" aria-hidden />
              {t("checkout.secureStripe")} — {t("checkout.cancelAnytime")}
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-white/[0.08] bg-white/[0.035] p-4 sm:mt-7 sm:p-5">
          <div className="space-y-4">
            {benefitGroups.map((group) => (
              <div key={group.title} className="flex gap-3">
                <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent-blue/[0.12] text-accent-blue">
                  {group.icon === "coins" ? (
                    <Coins className="size-4" aria-hidden />
                  ) : (
                    <Icon name={group.icon} size={16} className="size-4" />
                  )}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-black leading-5 text-white">{group.title}</p>
                  {group.details.length > 0 ? (
                    <ul className="mt-1 space-y-0.5">
                      {group.details.map((detail) => (
                        <li
                          key={detail}
                          className="flex items-center gap-2 text-xs font-medium leading-5 text-white/[0.48]"
                        >
                          <Icon name="tick" size={12} className="size-3 text-accent-blue" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export function SubscriptionPageContent({
  characterId,
  intent,
  onClose,
  paywallExperimentFlag,
  paywallExperimentVariant,
  presentation = "page",
  source = "subscription_page",
  stripeAmounts = null,
}: {
  characterId?: string | null
  intent?: string | null
  onClose?: () => void
  paywallExperimentFlag?: string | null
  paywallExperimentVariant?: string | null
  presentation?: CheckoutPresentation
  source?: CheckoutSource
  stripeAmounts?: PaywallAmounts | null
} = {}) {
  const t = useTranslations("pricing")
  const locale = useLocale()
  const intlLocale = getIntlLocale(locale)
  const router = useLocalizedRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const openSettings = useUIStore((s) => s.openSettings)
  const { isAuthenticated, user } = useAuth()
  const { subscription, isLoading: isSubscriptionLoading } = useSubscription()
  const queryClient = useQueryClient()
  const { isYearly, setIsYearly } = useBillingPeriodExperiment(source)
  const paywallCtaLoadingStateEnabled = usePaywallCtaLoadingState()
  const { variant: reassuranceVariant, showReassurance, eventProps: reassuranceEventProps } = useCheckoutReassuranceExperiment()
  const [loadingTier, setLoadingTier] = useState<string | null>(null)
  const [ctaErrorTier, setCtaErrorTier] = useState<string | null>(null)
  const [showDowngradeConfirm, setShowDowngradeConfirm] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedTierOverride, setSelectedTierOverride] = useState<PlanTier | null>(null)
  const [selectedTierSource, setSelectedTierSource] = useState<CheckoutSelectedFrom | null>(null)
  const [confidenceStripVariant, setConfidenceStripVariant] =
    useState<CheckoutConfidenceStripVariant>("unassigned")
  const cancelTrackedRef = useRef(false)

  const currentTier: SubscriptionTier = user?.subscription ?? "free"
  const isPaid = isPaidTier(currentTier)
  const isStripeBackedSubscription =
    Boolean(subscription?.stripe_subscription_id)
  const isCanceling = subscription?.cancel_at_period_end === true
  const isPastDue = isStripeBackedSubscription && subscription?.status === "past_due"
  const pendingTier = subscription?.pending_tier ?? null
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(intlLocale, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null
  const intentParam = parsePaywallIntent(intent ?? searchParams.get("intent"))
  const characterIdParam = parseOpaqueCharacterId(
    characterId ?? searchParams.get("characterId")
  )
  const rawPaywallExperimentFlagParam =
    paywallExperimentFlag ?? searchParams.get("paywallExperimentFlag")
  const rawPaywallExperimentVariantParam =
    paywallExperimentVariant ?? searchParams.get("paywallExperimentVariant")
  const normalizedPaywallExperiment = normalizePaywallExperiment(
    rawPaywallExperimentFlagParam,
    rawPaywallExperimentVariantParam
  )
  const paywallExperimentFlagParam = normalizedPaywallExperiment.flag
  const paywallExperimentVariantParam = normalizedPaywallExperiment.variant
  const paywallExperimentProperties = useMemo(
    () =>
      paywallExperimentFlagParam && paywallExperimentVariantParam
        ? {
            flag: paywallExperimentFlagParam,
            variant: paywallExperimentVariantParam,
            paywall_experiment_flag: paywallExperimentFlagParam,
            paywall_experiment_variant: paywallExperimentVariantParam,
          }
        : {},
    [paywallExperimentFlagParam, paywallExperimentVariantParam]
  )
  const tierParam = parsePlanTier(searchParams.get("tier"))
  const billingParam = parseBillingPeriod(searchParams.get("billing"))
  const checkoutSource = parseCheckoutSource(searchParams.get("source") ?? source)
  const checkoutSelectedFromParam = parseCheckoutSelectedFrom(searchParams.get("selectedFrom"))
  const cancelCheckoutAttemptId =
    parseOpaqueCharacterId(searchParams.get("checkout_attempt_id")) ?? undefined
  const normalizedCancelCheckoutReassuranceVariant = normalizeCheckoutReassuranceVariant(
    searchParams.get("checkout_reassurance_variant") ?? undefined
  )
  const cancelCheckoutReassuranceVariant =
    normalizedCancelCheckoutReassuranceVariant === "unassigned"
      ? undefined
      : normalizedCancelCheckoutReassuranceVariant
  const registerHrefFor = useCallback(
    (planTier: PlanTier) =>
      loggedOutRegisterHref({
        tier: planTier,
        billing: isYearly ? "yearly" : "monthly",
        source: checkoutSource,
        intent: intentParam,
        characterId: characterIdParam,
        paywallExperimentFlag: paywallExperimentFlagParam,
        paywallExperimentVariant: paywallExperimentVariantParam,
        presentation,
        pathname,
      }),
    [
      characterIdParam,
      checkoutSource,
      intentParam,
      isYearly,
      pathname,
      paywallExperimentFlagParam,
      paywallExperimentVariantParam,
      presentation,
    ]
  )
  const selectedTier = selectedTierOverride ?? toPlanTier(currentTier) ?? "premium"
  const selectedPlan =
    pricingPlans.find((plan) => plan.tier === selectedTier) ?? pricingPlans[1]!
  const selectedFrom: CheckoutSelectedFrom =
    selectedTierSource ?? (toPlanTier(currentTier) ? "current_subscription" : "default")

  useEffect(() => {
    track("sub:plan_view", { source })
  }, [source])

  useEffect(() => {
    const ph = getPostHogForConfidenceStrip()
    if (!ph) return
    const applyVariant = () => {
      setConfidenceStripVariant(
        normalizeConfidenceStripVariant(ph.getFeatureFlag(CHECKOUT_CONFIDENCE_STRIP_FLAG))
      )
    }
    applyVariant()
    const unsubscribe = ph.onFeatureFlags?.(applyVariant)
    return typeof unsubscribe === "function" ? unsubscribe : undefined
  }, [])

  useEffect(() => {
    if (tierParam) {
      setSelectedTierOverride(tierParam)
      setSelectedTierSource("query")
    } else {
      setSelectedTierOverride(null)
      setSelectedTierSource(null)
    }
  }, [tierParam])

  useEffect(() => {
    if (billingParam) {
      setIsYearly(billingParam === "yearly")
    } else {
      setIsYearly(true)
    }
  }, [billingParam, setIsYearly])

  useEffect(() => {
    if (cancelTrackedRef.current) return
    const canceled = searchParams.get("canceled") === "true" || searchParams.get("cancelled") === "true"
    if (!canceled) return
    cancelTrackedRef.current = true
    // Prefer the variant echoed back in the cancel URL (written at session-create time,
    // so it's always reliable). Fall back to the current PostHog state if not present.
    const cancelReassuranceProps = cancelCheckoutReassuranceVariant
      ? {
          checkout_reassurance_flag: CRO_CHECKOUT_REASSURANCE_FLAG,
          checkout_reassurance_variant: cancelCheckoutReassuranceVariant,
        }
      : reassuranceEventProps
    track("sub:checkout_cancel", {
      ...(tierParam ? { plan: tierParam, tier: tierParam } : {}),
      ...(billingParam ? { billing_interval: billingParam, billing_period: billingParam } : {}),
      source: checkoutSource,
      page_path: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...(intentParam ? { intent: intentParam } : {}),
      character_id: characterIdParam,
      ...(cancelCheckoutAttemptId ? { checkout_attempt_id: cancelCheckoutAttemptId } : {}),
      selected_from: checkoutSelectedFromParam ?? (tierParam ? "query" : selectedFrom),
      ...paywallExperimentProperties,
      ...cancelReassuranceProps,
    })
  }, [
    billingParam,
    cancelCheckoutAttemptId,
    cancelCheckoutReassuranceVariant,
    characterIdParam,
    checkoutSelectedFromParam,
    checkoutSource,
    intentParam,
    paywallExperimentProperties,
    paywallExperimentFlagParam,
    paywallExperimentVariantParam,
    reassuranceEventProps,
    searchParams,
    selectedFrom,
    tierParam,
  ])

  const handleSelectTier = useCallback(
    (tier: PlanTier) => {
      const plan = pricingPlans.find((p) => p.tier === tier)
      if (plan) {
        track("sub:plan_select", {
          tier,
          billing_period: isYearly ? "yearly" : "monthly",
          amount_cents: Math.round(plan.price * 100),
        })
      }
      setSelectedTierOverride(tier)
      setSelectedTierSource("manual")
    },
    [isYearly]
  )

  // ── Refresh auth store + React Query caches after subscription changes ────
  async function refreshAfterSubscriptionChange() {
    if (!user?.id) return
    // Invalidate all coin-related queries so hooks refetch
    queryClient.invalidateQueries({ queryKey: queryKeys.coins.balance(user.id) })
    queryClient.invalidateQueries({ queryKey: queryKeys.coins.subscription(user.id) })
    queryClient.invalidateQueries({ queryKey: queryKeys.coins.transactions(user.id) })
    // Refresh the Zustand auth store with latest profile data
    try {
      const result = await getProfileForCurrentUser()
      if (result.success && result.profile) {
        const { setUser, setProfile, setLoading } = useAuthStore.getState()
        const p = result.profile
        deferAuthStoreMutation(() => {
          setUser({
            id: p.id,
            email: p.email,
            name: p.display_name,
            avatar: p.avatar_url ?? undefined,
            subscription: p.subscription,
          })
          setProfile({
            username: p.username,
            usernameChangedAt: p.username_changed_at ?? null,
            bio: p.bio,
            ageVerified: p.age_verified,
            tosAccepted: p.tos_accepted,
            onboardingCompleted: p.onboarding_completed,
            chatDisplayName: p.chat_display_name,
            privacySettings: p.privacy_settings,
            chatCoinsUsed: p.chat_coins_used,
            imageGenerationsUsed: p.image_generations_used,
            contentPreferences: (p as unknown as Record<string, unknown>).content_preferences as string[] | null ?? null,
            preferredCharacterGender: (p as unknown as Record<string, unknown>).preferred_character_gender as "female" | "male" | "any" | null ?? null,
            preferredLocale: p.preferred_locale ?? null,
          })
          setLoading(false)
        })
      }
    } catch {
      // Non-critical — UI will refresh on next navigation
    }
  }

  // ── Determine card action for each tier ───────────────────────────────────

  function getCardAction(planTier: PlanTier): CardAction {
    const planName = t(`plans.${planMessageKeys[planTier]}.name`)
    if (!isAuthenticated) {
      return { type: "register", label: t("checkout.getStarted") }
    }
    if (currentTier === "free") {
      return {
        type: "subscribe",
        label: t("checkout.subscribeTo", { plan: planName }),
      }
    }

    // Paid user
    if (planTier === currentTier) {
      return { type: "current", label: t("checkout.currentPlan") }
    }

    if (isPastDue) {
      return { type: "disabled", label: t("checkout.fixPaymentFirst") }
    }
    // Only gate as non-Stripe once the subscription row has actually loaded.
    // While the query is in-flight `subscription` is null, which would otherwise
    // transiently flag every Stripe-backed paid user as "Managed outside Stripe".
    if (!isStripeBackedSubscription && isPaid && !isSubscriptionLoading && subscription) {
      return { type: "disabled", label: t("checkout.managedOutsideStripe") }
    }

    const direction = getTierDirection(currentTier, planTier)

    if (direction === "upgrade") {
      return { type: "upgrade", label: t("checkout.upgrade") }
    }

    if (direction === "downgrade") {
      // If this tier is the pending downgrade target
      if (pendingTier === planTier) {
        return {
          type: "pending_downgrade",
          label: t("checkout.changePending"),
          sublabel: t("checkout.cancelPlanChange"),
        }
      }
      // If already canceling, don't allow downgrade
      if (isCanceling) {
        return { type: "disabled", label: t("checkout.reactivateFirst") }
      }
      // If there's already a different pending downgrade, disable
      if (pendingTier && pendingTier !== planTier) {
        return { type: "disabled", label: t("checkout.changePending") }
      }
      return { type: "downgrade", label: t("checkout.downgrade") }
    }

    return { type: "current", label: t("checkout.currentPlan") }
  }

  // ── Handlers ────────────────────────────────────────────────────────────────

  async function handleSubscribe(planTier: "starter" | "premium" | "ultimate") {
    if (!isAuthenticated) {
      // Defensive guard: getCardAction returns "register" for unauthenticated users so this
      // branch should not be reachable in normal flow. Emit checkout_intent here anyway to
      // prevent a silent miss if auth state races between render and click.
      track("sub:checkout_intent", {
        plan: planTier,
        billing_interval: isYearly ? "yearly" : "monthly",
        source: checkoutSource,
        ...(intentParam ? { intent: intentParam } : {}),
        page_path: typeof window !== "undefined" ? window.location.pathname : "/subscription",
        checkout_attempt_id: crypto.randomUUID(),
        blocked_reason: "unauthenticated",
        ...paywallExperimentProperties,
        ...(confidenceStripVariant !== "unassigned" ? { confidence_strip_variant: confidenceStripVariant } : {}),
        ...reassuranceEventProps,
      })
      router.push(registerHrefFor(planTier))
      return
    }
    const billingPeriod = isYearly ? "yearly" : "monthly"
    const checkoutAttemptId = crypto.randomUUID()
    const pagePath = typeof window !== "undefined" ? window.location.pathname : "/subscription"
    const checkoutExperimentProps = {
      ...(paywallExperimentFlagParam && paywallExperimentVariantParam
        ? {
            flag: paywallExperimentFlagParam,
            variant: paywallExperimentVariantParam,
            paywall_experiment_flag: paywallExperimentFlagParam,
            paywall_experiment_variant: paywallExperimentVariantParam,
          }
        : {}),
      ...(confidenceStripVariant !== "unassigned"
        ? { confidence_strip_variant: confidenceStripVariant }
        : {}),
    }
    const allCheckoutExperimentProps = { ...checkoutExperimentProps, ...reassuranceEventProps }

    setLoadingTier(planTier)
    setCtaErrorTier(null)
    setActionError(null)
    const shouldShowCheckoutBannerError = !paywallCtaLoadingStateEnabled

    // Emit intent before the Stripe session request
    track("sub:checkout_intent", {
      plan: planTier,
      billing_interval: billingPeriod,
      source: checkoutSource,
      ...(intentParam ? { intent: intentParam } : {}),
      page_path: pagePath,
      checkout_attempt_id: checkoutAttemptId,
      ...allCheckoutExperimentProps,
    })

    try {
      const checkoutContext = {
        source: checkoutSource,
        ...(intentParam ? { intent: intentParam } : {}),
        characterId: characterIdParam,
        selectedFrom,
        paywallExperimentFlag: paywallExperimentFlagParam,
        paywallExperimentVariant: paywallExperimentVariantParam,
        checkoutAttemptId,
        ...(reassuranceVariant !== "unassigned"
          ? {
              checkoutReassuranceFlag: CRO_CHECKOUT_REASSURANCE_FLAG,
              checkoutReassuranceVariant: reassuranceVariant,
            }
          : {}),
      }
      const result = await createCheckoutSession(planTier, billingPeriod, checkoutContext)
      if (result.checkoutUrl) {
        // Fires just before the browser navigates away. emit_source distinguishes this
        // client-side event from the server_action capture in actions.ts — PostHog
        // funnels can filter by emit_source to avoid double-counting.
        track("sub:checkout_start", {
          plan: planTier,
          billing_interval: billingPeriod,
          emit_source: "client_redirect",
          source: checkoutSource,
          ...(intentParam ? { intent: intentParam } : {}),
          page_path: pagePath,
          checkout_attempt_id: checkoutAttemptId,
          selected_from: selectedFrom,
          ...allCheckoutExperimentProps,
        })
        noteCheckoutStarted(browserLocalStorage(), checkoutAttemptId, Date.now(), user?.id ?? null)
        window.location.href = result.checkoutUrl
        return
      }
      if (result.error) {
        track("sub:checkout_error", {
          plan: planTier,
          billing_interval: billingPeriod,
          source: checkoutSource,
          ...(intentParam ? { intent: intentParam } : {}),
          page_path: pagePath,
          checkout_attempt_id: checkoutAttemptId,
          error_stage: "server_action",
          error_code: "server_action_error",
          ...allCheckoutExperimentProps,
        })
        setCtaErrorTier(planTier)
        if (shouldShowCheckoutBannerError) {
          setActionError(result.error)
        }
        return
      }
      track("sub:checkout_error", {
        plan: planTier,
        billing_interval: billingPeriod,
        source: checkoutSource,
        ...(intentParam ? { intent: intentParam } : {}),
        page_path: pagePath,
        checkout_attempt_id: checkoutAttemptId,
        error_stage: "missing_checkout_url",
        error_code: "missing_checkout_url",
        ...allCheckoutExperimentProps,
      })
      setCtaErrorTier(planTier)
      if (shouldShowCheckoutBannerError) {
        setActionError(t("checkout.checkoutStartError"))
      }
    } catch (err) {
      track("sub:checkout_error", {
        plan: planTier,
        billing_interval: billingPeriod,
        source: checkoutSource,
        ...(intentParam ? { intent: intentParam } : {}),
        page_path: pagePath,
        checkout_attempt_id: checkoutAttemptId,
        error_stage: "unexpected_exception",
        error_code: "unexpected_exception",
        ...allCheckoutExperimentProps,
      })
      setCtaErrorTier(planTier)
      if (shouldShowCheckoutBannerError) {
        setActionError(err instanceof Error ? err.message : t("checkout.genericError"))
      }
    } finally {
      setLoadingTier(null)
    }
  }

  useEffect(() => {
    if (!ctaErrorTier) return
    const timeoutId = window.setTimeout(() => setCtaErrorTier(null), 2000)
    return () => window.clearTimeout(timeoutId)
  }, [ctaErrorTier])

  async function handleUpgrade(planTier: "starter" | "premium" | "ultimate") {
    const billingPeriod = isYearly ? "yearly" : "monthly"
    setLoadingTier(planTier)
    setActionError(null)
    try {
      const result = await changeSubscriptionTier(planTier, billingPeriod)
      if (result.success) {
        await refreshAfterSubscriptionChange()
        openSettings("billing", { source: "billing" })
      } else {
        setActionError(result.error ?? t("checkout.genericError"))
      }
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setLoadingTier(null)
    }
  }

  async function handleDowngradeConfirm(planTier: "starter" | "premium" | "ultimate") {
    const billingPeriod = isYearly ? "yearly" : "monthly"
    setLoadingTier(planTier)
    setActionError(null)
    setShowDowngradeConfirm(null)
    try {
      const result = await changeSubscriptionTier(planTier, billingPeriod)
      if (result.success) {
        await refreshAfterSubscriptionChange()
      } else {
        setActionError(result.error ?? t("checkout.genericError"))
      }
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setLoadingTier(null)
    }
  }

  async function handleCancelPendingDowngrade() {
    setLoadingTier("cancel_pending")
    setActionError(null)
    try {
      const result = await cancelPendingDowngrade()
      if (result.success) {
        await refreshAfterSubscriptionChange()
      } else {
        setActionError(result.error ?? t("checkout.genericError"))
      }
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setLoadingTier(null)
    }
  }

  async function handleReactivate() {
    setLoadingTier("reactivate")
    setActionError(null)
    try {
      const result = await reactivateSubscription()
      if (result.success) {
        await refreshAfterSubscriptionChange()
      } else {
        setActionError(result.error ?? t("checkout.genericError"))
      }
    } catch (err) {
      setActionError((err as Error).message)
    } finally {
      setLoadingTier(null)
    }
  }

  async function handleFixPayment() {
    setLoadingTier("fix_payment")
    try {
      const result = await createCustomerPortalSession()
      if (result.portalUrl) {
        window.location.href = result.portalUrl
      }
    } catch {
      setActionError(t("checkout.genericError"))
    } finally {
      setLoadingTier(null)
    }
  }

  function handleCardClick(plan: (typeof pricingPlans)[0]) {
    const tier = plan.tier as "starter" | "premium" | "ultimate"
    const action = getCardAction(tier)

    switch (action.type) {
      case "register": {
        // User clicked a plan card but is unauthenticated — emit checkout_intent with
        // blocked_reason so PostHog can separate eligibility blocks from page bounces.
        // Include the full experiment context (confidence strip + reassurance) so
        // checkout_intent events are sliceable by the same dimensions as authenticated paths.
        const blockedCheckoutExperimentProps = {
          ...paywallExperimentProperties,
          ...(confidenceStripVariant !== "unassigned" ? { confidence_strip_variant: confidenceStripVariant } : {}),
          ...reassuranceEventProps,
        }
        track("sub:checkout_intent", {
          plan: tier,
          billing_interval: isYearly ? "yearly" : "monthly",
          source: checkoutSource,
          ...(intentParam ? { intent: intentParam } : {}),
          page_path: typeof window !== "undefined" ? window.location.pathname : "/subscription",
          checkout_attempt_id: crypto.randomUUID(),
          blocked_reason: "unauthenticated",
          ...blockedCheckoutExperimentProps,
        })
        router.push(registerHrefFor(tier))
        break
      }
      case "subscribe":
        handleSubscribe(tier)
        break
      case "upgrade":
        handleUpgrade(tier)
        break
      case "downgrade":
        setShowDowngradeConfirm(tier)
        break
      case "pending_downgrade":
        handleCancelPendingDowngrade()
        break
      case "current":
      case "disabled":
        break
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  function getAnnualTotal(plan: (typeof pricingPlans)[0]) {
    return stripeAmounts?.[plan.tier as PlanTier]?.annual
      ?? Math.round(getYearlyMonthlyPrice(plan.price) * 12 * 100) / 100
  }

  function handleExit() {
    track("sub:exit_click", {
      source: checkoutSource,
      page_path: typeof window !== "undefined" ? window.location.pathname : "/subscription",
    })

    if (presentation === "modal" && onClose) {
      onClose()
      return
    }

    if (typeof window !== "undefined" && window.history.length > 1) {
      router.back()
      return
    }

    router.push(ROUTES.explore)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-[#080808] text-foreground",
        presentation === "modal"
          ? "max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain rounded-[1.5rem] pb-28 sm:max-h-[calc(100dvh-2rem)] sm:pb-6"
          : "min-h-screen pb-36 sm:pb-16"
      )}
    >
      <button
        type="button"
        onClick={handleExit}
        aria-label={t("checkout.exitPricing")}
        className={cn(
          "z-40 flex size-11 items-center justify-center rounded-full border border-white/[0.10] bg-white/[0.08] text-white/[0.74] shadow-[0_12px_40px_rgba(0,0,0,0.28)] transition-colors hover:border-white/[0.18] hover:bg-white/[0.14] hover:text-white focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-ring",
          presentation === "modal"
            ? "absolute right-3 top-3 sm:right-4 sm:top-4"
            : "fixed right-[max(1rem,env(safe-area-inset-right))] top-[max(1rem,env(safe-area-inset-top))]"
        )}
      >
        <Icon name="x" size={18} className="size-[1.125rem]" aria-hidden />
      </button>

      <UpgradeCheckoutPanel
        action={getCardAction(selectedTier)}
        annualTotal={getAnnualTotal(selectedPlan)}
        confidenceStripVariant={confidenceStripVariant}
        ctaErrorTier={ctaErrorTier}
        isPaid={isPaid}
        isYearly={isYearly}
        loadingTier={loadingTier}
        onAction={() => handleCardClick(selectedPlan)}
        onSelectTier={handleSelectTier}
        paywallCtaLoadingStateEnabled={paywallCtaLoadingStateEnabled}
        presentation={presentation}
        selectedPlan={selectedPlan}
        selectedTier={selectedTier}
        setIsYearly={setIsYearly}
        showReassurance={showReassurance}
        stripeAmounts={stripeAmounts}
      />

      {/* Status Banners */}
      <div className="mx-auto max-w-7xl px-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] lg:px-8">
        {/* Error banner */}
        {actionError && (
          <div
            className="mt-6 flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            <Icon name="attention-triangle" size={16} className="size-4 shrink-0" />
            <span className="flex-1">{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-300">
              <Icon name="x" size={16} className="size-4" />
            </button>
          </div>
        )}

        {/* Past due banner */}
        {isPastDue && (
          <div
            className="surface-subtle mt-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-foreground/90"
          >
            <Icon name="attention-triangle" size={16} className="size-4 shrink-0" />
            <span className="flex-1">
              {t("checkout.paymentFailed")}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-border text-foreground hover:bg-foreground/[0.06]"
              disabled={loadingTier === "fix_payment"}
              onClick={handleFixPayment}
            >
              {loadingTier === "fix_payment" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                t("checkout.updatePayment")
              )}
            </Button>
          </div>
        )}

        {/* Canceling banner */}
        {isCanceling && isStripeBackedSubscription && !isPastDue && (
          <div
            className="surface-subtle mt-6 flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-foreground/90"
          >
            <Icon name="clock" size={16} className="size-4 shrink-0" />
            <span className="flex-1">
              {t("checkout.cancelingBanner", { date: periodEnd ?? "" })}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-border text-foreground hover:bg-foreground/[0.06]"
              disabled={loadingTier === "reactivate"}
              onClick={handleReactivate}
            >
              {loadingTier === "reactivate" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                t("checkout.reactivate")
              )}
            </Button>
          </div>
        )}

        {/* Pending downgrade banner */}
        {pendingTier && isStripeBackedSubscription && !isCanceling && !isPastDue && (
          <div
            className="mt-6 flex items-center gap-3 rounded-xl border border-accent-blue/30 bg-accent-blue/10 px-4 py-3 text-sm text-accent-blue"
          >
            <Icon name="arrow-down" size={16} className="size-4 shrink-0" />
            <span className="flex-1">
              {t("checkout.pendingChange", {
                plan: t(`plans.${planMessageKeys[pendingTier as PlanTier]}.name`),
                date: periodEnd ?? "",
              })}
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 border-accent-blue/40 text-accent-blue hover:bg-accent-blue/10"
              disabled={loadingTier === "cancel_pending"}
              onClick={handleCancelPendingDowngrade}
            >
              {loadingTier === "cancel_pending" ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                t("checkout.cancelChange")
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Pricing cards — desktop only */}
      {presentation === "page" ? (
        <section className="mx-auto max-w-3xl px-4 py-8 lg:px-8">
          <h2 className="mb-4 text-xl font-semibold text-foreground">
            {t("checkout.questions")}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqKeys.map((faqKey) => (
              <AccordionItem key={faqKey} value={`faq-${faqKey}`}>
                <AccordionTrigger className="text-left text-base">
                  {t(`faqs.${faqKey}.question`)}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/[0.62] leading-relaxed">
                  {t(`faqs.${faqKey}.answer`)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      {/* Downgrade Confirmation Modal */}
      {showDowngradeConfirm && (
        <DowngradeConfirmModal
          currentTier={currentTier}
          targetTier={showDowngradeConfirm as SubscriptionTier}
          periodEnd={periodEnd}
          isLoading={loadingTier === showDowngradeConfirm}
          onConfirm={() => handleDowngradeConfirm(showDowngradeConfirm as "starter" | "premium" | "ultimate")}
          onCancel={() => setShowDowngradeConfirm(null)}
        />
      )}
    </div>
  )
}

// ── Card Action Button ────────────────────────────────────────────────────────

export function SubscriptionCheckoutModal({
  characterId,
  intent,
  onOpenChange,
  open,
  paywallExperimentFlag,
  paywallExperimentVariant,
}: {
  characterId?: string | null
  intent?: string | null
  onOpenChange: (open: boolean) => void
  open: boolean
  paywallExperimentFlag?: string | null
  paywallExperimentVariant?: string | null
}) {
  const t = useTranslations("pricing")
  // Same live Stripe amounts /pricing renders, so both surfaces show one savings figure.
  const { data: stripeAmounts } = useQuery({
    queryKey: queryKeys.paywallAmounts(),
    queryFn: fetchPaywallAmounts,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  })
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="w-[calc(100vw-1rem)] max-w-[44rem] gap-0 overflow-hidden border-0 bg-transparent p-0 shadow-none sm:w-[calc(100vw-2rem)]"
      >
        <DialogTitle className="sr-only">{t("checkout.modalTitle")}</DialogTitle>
        <DialogDescription className="sr-only">
          {t("checkout.modalDescription")}
        </DialogDescription>
        {open ? (
          <Suspense fallback={null}>
            <SubscriptionPageContent
              characterId={characterId}
              intent={intent}
              onClose={() => onOpenChange(false)}
              paywallExperimentFlag={paywallExperimentFlag}
              paywallExperimentVariant={paywallExperimentVariant}
              presentation="modal"
              source="paywall_modal"
              stripeAmounts={stripeAmounts ?? null}
            />
          </Suspense>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

async function fetchPaywallAmounts(): Promise<PaywallAmounts | null> {
  const response = await fetch("/api/paywall-amounts")
  if (!response.ok) return null
  return parsePaywallAmountsResponse(await response.json())
}

function formatUsd(amount: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function formatNumber(amount: number, locale: string) {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

function CardActionButton({
  action,
  planTier,
  ctaErrorTier,
  isHighlighted,
  isCurrentPlan,
  loadingTier,
  onClick,
  paywallCtaLoadingStateEnabled,
  labelOverride,
  className,
}: {
  action: CardAction
  planTier: SubscriptionTier
  ctaErrorTier: string | null
  isHighlighted: boolean
  isCurrentPlan: boolean
  loadingTier: string | null
  onClick: () => void
  paywallCtaLoadingStateEnabled: boolean
  labelOverride?: string
  className?: string
}) {
  const t = useTranslations("pricing")
  const isLoading = loadingTier === planTier
  const isSubscribeCheckout = action.type === "subscribe"
  const showCtaError = paywallCtaLoadingStateEnabled && isSubscribeCheckout && ctaErrorTier === planTier
  const loadingLabel =
    paywallCtaLoadingStateEnabled && isSubscribeCheckout
      ? t("checkout.openingCheckout")
      : t("checkout.redirecting")
  const displayLabel = labelOverride ?? action.label
  const isPendingDowngradeLoading =
    action.type === "pending_downgrade" && loadingTier === "cancel_pending"
  const isActionLoading = isPendingDowngradeLoading || isLoading
  const isHighlightedCheckoutAction =
    action.type === "subscribe" ||
    action.type === "register" ||
    action.type === "upgrade" ||
    action.type === "downgrade" ||
    action.type === "pending_downgrade"
  const highlightedLoadingLabel =
    action.type === "upgrade"
      ? t("checkout.upgrading")
      : action.type === "downgrade" || action.type === "pending_downgrade"
        ? t("checkout.processing")
        : loadingLabel

  if (action.type === "current") {
    return (
      <Button
        className={cn(
          "w-full",
          isCurrentPlan
            ? "cursor-default border border-white/20 bg-foreground/[0.08] text-foreground"
            : "",
          className
        )}
        variant="outline"
        size="lg"
        disabled
      >
        {displayLabel}
      </Button>
    )
  }

  if (action.type === "disabled") {
    return (
      <Button className={cn("w-full", className)} variant="outline" size="lg" disabled>
        {displayLabel}
      </Button>
    )
  }

  if (isHighlighted && isHighlightedCheckoutAction) {
    return (
      <Button
        className={cn("relative w-full", className)}
        size="lg"
        disabled={!!loadingTier}
        onClick={onClick}
      >
        <span className="pointer-events-none absolute inset-0 bg-[radial-gradient(72%_140%_at_50%_120%,rgba(140,197,255,0.38)_0%,rgba(29,125,242,0.18)_42%,rgba(29,125,242,0)_74%)] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        <span className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-[linear-gradient(180deg,rgba(255,255,255,0.24)_0%,rgba(255,255,255,0.075)_72%,rgba(255,255,255,0)_100%)] opacity-90 transition-opacity duration-200 group-hover:opacity-80" />
        <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-white/70 transition-opacity duration-200 group-hover:opacity-100" />
        <span className="pointer-events-none absolute inset-x-6 bottom-0 h-px bg-accent-blue/60 opacity-50 transition-opacity duration-200 group-hover:opacity-100" />
        <span className="pointer-events-none absolute inset-[1px] rounded-full border border-white/[0.12] opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        <span className="relative z-10 inline-flex items-center justify-center gap-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.48)]">
          {showCtaError ? (
            t("checkout.tryAgain")
          ) : isActionLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {highlightedLoadingLabel}
            </>
          ) : (
            displayLabel
          )}
        </span>
      </Button>
    )
  }

  if (action.type === "pending_downgrade") {
    return (
      <Button
        className={cn("w-full border-accent-blue/40 text-accent-blue hover:bg-accent-blue/10", className)}
        variant="outline"
        size="lg"
        disabled={loadingTier === "cancel_pending"}
        onClick={onClick}
      >
        {loadingTier === "cancel_pending" ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            {t("checkout.processing")}
          </>
        ) : (
          labelOverride ?? action.sublabel
        )}
      </Button>
    )
  }

  if (action.type === "upgrade") {
    return (
      <Button
        className={cn("w-full", className)}
        size="lg"
        disabled={!!loadingTier}
        onClick={onClick}
      >
        {isLoading ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            {t("checkout.upgrading")}
          </>
        ) : (
          displayLabel
        )}
      </Button>
    )
  }

  if (action.type === "downgrade") {
    return (
      <Button
        className={cn("w-full", className)}
        variant="outline"
        size="lg"
        disabled={!!loadingTier}
        onClick={onClick}
      >
        {isLoading ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            {t("checkout.processing")}
          </>
        ) : (
          displayLabel
        )}
      </Button>
    )
  }

  // subscribe or register
  if (isHighlighted) {
    return (
      <Button
        className={cn("w-full bg-white text-black hover:bg-white/90", className)}
        size="lg"
        disabled={!!loadingTier}
        onClick={onClick}
      >
        {showCtaError ? (
          t("checkout.tryAgain")
        ) : isLoading ? (
          <>
            <Loader2 className="size-4 animate-spin mr-2" />
            {loadingLabel}
          </>
        ) : (
          displayLabel
        )}
      </Button>
    )
  }

  return (
    <Button
      className={cn("w-full", className)}
      variant="outline"
      size="lg"
      disabled={!!loadingTier}
      onClick={onClick}
    >
      {showCtaError ? (
        t("checkout.tryAgain")
      ) : isLoading ? (
        <>
          <Loader2 className="size-4 animate-spin mr-2" />
          {loadingLabel}
        </>
      ) : (
        displayLabel
      )}
    </Button>
  )
}

// ── Downgrade Confirmation Modal ──────────────────────────────────────────────

function DowngradeConfirmModal({
  currentTier,
  targetTier,
  periodEnd,
  isLoading,
  onConfirm,
  onCancel,
}: {
  currentTier: SubscriptionTier
  targetTier: SubscriptionTier
  periodEnd: string | null
  isLoading: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const t = useTranslations("pricing")
  const locale = useLocale()
  const intlLocale = getIntlLocale(locale)
  const currentConfig = TIER_CONFIG[currentTier]
  const targetConfig = TIER_CONFIG[targetTier]

  // Determine what the user loses
  const lostFeatures: string[] = []
  const coinDiff = currentConfig.monthlyCoins - targetConfig.monthlyCoins
  if (coinDiff > 0) {
    lostFeatures.push(t("checkout.coinsPerMonth", { count: coinDiff.toLocaleString(intlLocale) }))
  }
  if (currentConfig.hasPhoneCall && !targetConfig.hasPhoneCall) {
    lostFeatures.push(t("checkout.phoneCalls"))
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/70"
        onClick={onCancel}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="glass-panel relative z-10 w-full max-w-md rounded-2xl p-6"
      >
        <div className="mb-6 flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-foreground/[0.08]">
            <Icon name="arrow-down" size={20} className="size-5 text-foreground/80" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {t("checkout.downgradeTitle", {
                plan: t(`plans.${planMessageKeys[targetTier as PlanTier]}.name`),
              })}
            </h3>
            <p className="text-sm text-muted-foreground">
              {periodEnd
                ? t("checkout.changeOn", { date: periodEnd })
                : t("checkout.changeNextCycle")}
            </p>
          </div>
        </div>

        {/* What you'll lose */}
        {lostFeatures.length > 0 && (
          <div className="surface-subtle mb-6 rounded-xl p-4">
            <p className="mb-2 text-sm font-medium text-foreground/90">
              {t("checkout.loseAccess")}
            </p>
            <ul className="space-y-1.5">
              {lostFeatures.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="x" size={14} className="size-3.5 text-foreground/60" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What you keep */}
        <div className="mb-6 rounded-xl border border-border/40 bg-muted/20 p-4">
          <p className="mb-2 text-sm font-medium text-foreground">
            {t("checkout.keepUntil", { date: periodEnd ?? t("checkout.periodEnd") })}
          </p>
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="tick" size={14} className="size-3.5 text-emerald-400" />
              {t("checkout.fullCurrentPlan", {
                plan: t(`plans.${planMessageKeys[currentTier as PlanTier]}.name`),
              })}
            </li>
            <li className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="tick" size={14} className="size-3.5 text-emerald-400" />
              {t("checkout.allRemainingCoins")}
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("checkout.keepPlan", {
              plan: t(`plans.${planMessageKeys[currentTier as PlanTier]}.name`),
            })}
          </Button>
          <Button
            variant="outline"
            className="flex-1 border-border text-foreground hover:bg-foreground/[0.06]"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                {t("checkout.processing")}
              </>
            ) : (
              t("checkout.confirmDowngrade")
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  )
}
